import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { 
  Package, 
  Plus, 
  X, 
  Search, 
  Filter,
  CheckCircle,
  AlertCircle,
  Truck,
  User,
  Calendar,
  DollarSign,
  Box,
  ArrowDownCircle,
  ArrowUpCircle,
  Eye,
  Edit,
  Trash2,
  ChevronDown,
  ClipboardList,
  Shield,
  PenTool,
  FileText
} from 'lucide-react'
import './SupplyChain.css'

const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api'

const categories = [
  { id: 'Uniform', label: 'Uniform Inventory', icon: Shield, color: '#1a237e' },
  { id: 'Stationery', label: 'Stationery', icon: PenTool, color: '#4caf50' },
  { id: 'Equipment', label: 'Equipment', icon: Box, color: '#ff9800' },
  { id: 'Safety Gear', label: 'Safety Gear', icon: Shield, color: '#f44336' },
  { id: 'Vehicle', label: 'Vehicle', icon: Truck, color: '#2196f3' },
  { id: 'Furniture', label: 'Furniture', icon: Box, color: '#795548' },
  { id: 'Electronics', label: 'Electronics', icon: Box, color: '#9c27b0' }
]

const uniformSubCategories = ['Shirt', 'Trouser', 'Jacket', 'Boots', 'Cap', 'Belt', 'Badge', 'Whistle', 'Torch', 'Rain Coat']
const stationerySubCategories = ['Pen', 'Pencil', 'Paper', 'Envelope', 'Stapler', 'File', 'Folder', 'Marker', 'Tape', 'Calculator']
const equipmentSubCategories = ['Radio', 'Phone', 'Camera', 'Printer', 'Computer', 'Scanner']
const safetySubCategories = ['Helmet', 'Vest', 'Gloves', 'Boots', 'Goggles', 'First Aid Kit']
const vehicleSubCategories = ['Car', 'Van', 'Truck', 'Motorcycle', 'Bus', 'Ambulance', 'Patrol Vehicle', 'Fire Truck']
const furnitureSubCategories = ['Desk', 'Chair', 'Cabinet', 'Shelf', 'Table', 'Sofa', 'Locker', 'Bed', 'Filing Cabinet']
const electronicsSubCategories = ['Computer', 'Laptop', 'Monitor', 'Printer', 'Scanner', 'Router', 'Switch', 'UPS', 'Projector', 'Camera']

const conditionOptions = ['New', 'Good', 'Fair', 'Poor', 'Damaged']

const SupplyChain = () => {
  const [activeTab, setActiveTab] = useState('Uniform')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [showDistributeModal, setShowDistributeModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [viewItem, setViewItem] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [message, setMessage] = useState({ type: '', text: '' })
  const [guards, setGuards] = useState([])
  const [stats, setStats] = useState([])

  const [formData, setFormData] = useState({
    itemName: '',
    itemCode: '',
    category: 'Uniform',
    subCategory: '',
    description: '',
    size: '',
    color: '',
    reorderLevel: 10,
    storageLocation: 'Main Store'
  })

  const [purchaseData, setPurchaseData] = useState({
    purchaseDate: '',
    supplierName: '',
    quantity: 1,
    unitCost: 0,
    invoiceNumber: '',
    notes: ''
  })

  const [distributeData, setDistributeData] = useState({
    distributedTo: '',
    distributedToId: '',
    quantity: 1,
    signedOffBy: '',
    signedOffById: '',
    purpose: '',
    condition: 'Good',
    notes: ''
  })

  useEffect(() => {
    fetchItems()
    fetchGuards()
    fetchStats()
  }, [activeTab])

  const showMessage = (type, text) => {
    setMessage({ type, text })
    setTimeout(() => setMessage({ type: '', text: '' }), 4000)
  }

  const fetchItems = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${API_URL}/inventory?category=${activeTab}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setItems(res.data.data || [])
    } catch (err) {
      showMessage('error', 'Failed to load inventory')
    } finally {
      setLoading(false)
    }
  }

  const fetchGuards = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${API_URL}/guards`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setGuards(res.data.data || [])
    } catch (err) {
      console.log('Failed to load guards')
    }
  }

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await axios.get(`${API_URL}/inventory/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setStats(res.data.data || [])
    } catch (err) {
      console.log('Failed to load stats')
    }
  }

  const handleCreateItem = async (e) => {
    e.preventDefault()
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/inventory`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      showMessage('success', 'Item created successfully')
      setShowModal(false)
      resetForm()
      fetchItems()
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to create item')
    }
  }

  const handlePurchase = async (e) => {
    e.preventDefault()
    if (!selectedItem) return
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/inventory/${selectedItem._id}/purchase`, purchaseData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      showMessage('success', 'Purchase recorded')
      setShowPurchaseModal(false)
      setPurchaseData({
        purchaseDate: '', supplierName: '', quantity: 1, unitCost: 0, invoiceNumber: '', notes: ''
      })
      fetchItems()
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to record purchase')
    }
  }

  const handleDistribute = async (e) => {
    e.preventDefault()
    if (!selectedItem) return
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/inventory/${selectedItem._id}/distribute`, distributeData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      showMessage('success', 'Distribution recorded')
      setShowDistributeModal(false)
      setDistributeData({
        distributedTo: '', distributedToId: '', quantity: 1, signedOffBy: '', signedOffById: '',
        purpose: '', condition: 'Good', notes: ''
      })
      fetchItems()
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to distribute')
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this item?')) return
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/inventory/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      showMessage('success', 'Item deleted')
      fetchItems()
    } catch (err) {
      showMessage('error', 'Failed to delete')
    }
  }

  const openPurchaseModal = (item) => {
    setSelectedItem(item)
    setPurchaseData({
      purchaseDate: new Date().toISOString().split('T')[0],
      supplierName: '', quantity: 1, unitCost: 0, invoiceNumber: '', notes: ''
    })
    setShowPurchaseModal(true)
  }

  const openDistributeModal = (item) => {
    setSelectedItem(item)
    setDistributeData({
      distributedTo: '', distributedToId: '', quantity: 1, signedOffBy: '', signedOffById: '',
      purpose: '', condition: 'Good', notes: ''
    })
    setShowDistributeModal(true)
  }

  const resetForm = () => {
    setFormData({
      itemName: '', itemCode: '', category: activeTab, subCategory: '', description: '',
      size: '', color: '', reorderLevel: 10, storageLocation: 'Main Store'
    })
  }

  const filteredItems = items.filter(item => 
    item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.itemCode?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getSubCategories = () => {
    switch(activeTab) {
      case 'Uniform': return uniformSubCategories
      case 'Stationery': return stationerySubCategories
      case 'Equipment': return equipmentSubCategories
      case 'Safety Gear': return safetySubCategories
      case 'Vehicle': return vehicleSubCategories
      case 'Furniture': return furnitureSubCategories
      case 'Electronics': return electronicsSubCategories
      default: return []
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleDateString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    } catch {
      return 'Invalid Date'
    }
  }

  return (
    <div className="inventory-module">
      <div className="inv-header">
        <div>
          <h1><Package size={28} /> Inventory Management</h1>
          <p>Track purchases, distribution & stock levels</p>
        </div>
        <button className="inv-btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <Plus size={18} /> Add Item
        </button>
      </div>

      {message.text && (
        <div className={`inv-alert inv-alert-${message.type}`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Category Tabs */}
      <div className="inv-tabs">
        {categories.map(cat => {
          const Icon = cat.icon
          const isActive = activeTab === cat.id
          return (
            <button
              key={cat.id}
              className={`inv-tab ${isActive ? 'active' : ''}`}
              onClick={() => setActiveTab(cat.id)}
              style={isActive ? { borderColor: cat.color, color: cat.color } : {}}
            >
              <Icon size={18} />
              <span>{cat.label}</span>
              <span className="inv-tab-count">
                {items.filter(i => i.category === cat.id).length}
              </span>
            </button>
          )
        })}
      </div>

      {/* Stats Cards */}
      <div className="inv-stats">
        <div className="inv-stat-card" style={{ borderLeftColor: '#4caf50' }}>
          <span className="inv-stat-number">{items.reduce((sum, i) => sum + (i.remainingStock || 0), 0)}</span>
          <span className="inv-stat-label">Total Stock</span>
        </div>
        <div className="inv-stat-card" style={{ borderLeftColor: '#ff9800' }}>
          <span className="inv-stat-number">{items.filter(i => (i.remainingStock || 0) <= (i.reorderLevel || 10)).length}</span>
          <span className="inv-stat-label">Low Stock</span>
        </div>
        <div className="inv-stat-card" style={{ borderLeftColor: '#2196f3' }}>
          <span className="inv-stat-number">{items.reduce((sum, i) => sum + (i.totalPurchased || 0), 0)}</span>
          <span className="inv-stat-label">Total Purchased</span>
        </div>
        <div className="inv-stat-card" style={{ borderLeftColor: '#9c27b0' }}>
          <span className="inv-stat-number">{items.reduce((sum, i) => sum + (i.totalDistributed || 0), 0)}</span>
          <span className="inv-stat-label">Total Distributed</span>
        </div>
      </div>

      {/* Search */}
      <div className="inv-search-bar">
        <Search size={16} />
        <input 
          type="text" 
          placeholder={`Search ${activeTab.toLowerCase()} items...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Items Grid */}
      {loading ? (
        <div className="inv-loading">Loading inventory...</div>
      ) : filteredItems.length === 0 ? (
        <div className="inv-empty">
          <Package size={48} opacity={0.3} />
          <p>No {activeTab.toLowerCase()} items found</p>
          <button className="inv-btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus size={16} /> Add First Item
          </button>
        </div>
      ) : (
        <div className="inv-items-grid">
          {filteredItems.map(item => {
            const isLowStock = (item.remainingStock || 0) <= (item.reorderLevel || 10)
            return (
              <div key={item._id} className={`inv-item-card ${isLowStock ? 'low-stock' : ''}`}>
                <div className="inv-item-header">
                  <div className="inv-item-icon" style={{ 
                    backgroundColor: categories.find(c => c.id === item.category)?.color + '15',
                    color: categories.find(c => c.id === item.category)?.color 
                  }}>
                    <Package size={20} />
                  </div>
                  <div className="inv-item-title">
                    <h4>{item.itemName}</h4>
                    <span className="inv-item-code">{item.itemCode || 'No Code'}</span>
                  </div>
                  <span className={`inv-stock-badge ${isLowStock ? 'warning' : 'good'}`}>
                    {item.remainingStock || 0} left
                  </span>
                </div>

                <div className="inv-item-details">
                  <div className="inv-detail-row">
                    <span><Box size={12} /> {item.subCategory || 'N/A'}</span>
                    <span><Calendar size={12} /> {formatDate(item.purchaseLogs?.[item.purchaseLogs.length - 1]?.purchaseDate)}</span>
                  </div>
                  <div className="inv-detail-row">
                    <span><DollarSign size={12} /> KES {item.purchaseLogs?.[item.purchaseLogs.length - 1]?.unitCost || 0}</span>
                    <span><Truck size={12} /> {item.purchaseLogs?.[item.purchaseLogs.length - 1]?.supplierName || 'N/A'}</span>
                  </div>
                </div>

                <div className="inv-stock-bar">
                  <div 
                    className="inv-stock-fill" 
                    style={{ 
                      width: `${Math.min(100, ((item.totalPurchased || 1) > 0 ? ((item.remainingStock || 0) / (item.totalPurchased || 1)) * 100 : 0))}%`,
                      backgroundColor: isLowStock ? '#f44336' : '#4caf50'
                    }}
                  />
                </div>

                <div className="inv-item-actions">
                  <button className="inv-action-btn" onClick={() => setViewItem(item)} title="View Details">
                    <Eye size={14} />
                  </button>
                  <button className="inv-action-btn purchase" onClick={() => openPurchaseModal(item)} title="Record Purchase">
                    <ArrowDownCircle size={14} />
                  </button>
                  <button className="inv-action-btn distribute" onClick={() => openDistributeModal(item)} title="Distribute/Issue">
                    <ArrowUpCircle size={14} />
                  </button>
                  <button className="inv-action-btn delete" onClick={() => handleDelete(item._id)} title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Add Item Modal */}
      {showModal && (
        <div className="inv-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="inv-modal">
            <div className="inv-modal-header">
              <h3><Plus size={20} /> Add {activeTab} Item</h3>
              <button onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateItem} className="inv-form">
              <div className="inv-form-row">
                <div className="inv-form-group">
                  <label>Item Name *</label>
                  <input value={formData.itemName} onChange={(e) => setFormData({...formData, itemName: e.target.value})} required />
                </div>
                <div className="inv-form-group">
                  <label>Item Code</label>
                  <input value={formData.itemCode} onChange={(e) => setFormData({...formData, itemCode: e.target.value})} placeholder="e.g., UNI-SH-001" />
                </div>
              </div>
              <div className="inv-form-row">
                <div className="inv-form-group">
                  <label>Category *</label>
                  <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div className="inv-form-group">
                  <label>Sub Category *</label>
                  <select value={formData.subCategory} onChange={(e) => setFormData({...formData, subCategory: e.target.value})} required>
                    <option value="">Select...</option>
                    {getSubCategories().map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="inv-form-row">
                <div className="inv-form-group">
                  <label>Size</label>
                  <input value={formData.size} onChange={(e) => setFormData({...formData, size: e.target.value})} placeholder="S, M, L, XL" />
                </div>
                <div className="inv-form-group">
                  <label>Color</label>
                  <input value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} placeholder="e.g., Navy Blue" />
                </div>
              </div>
              <div className="inv-form-group inv-form-full">
                <label>Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} />
              </div>
              <div className="inv-form-row">
                <div className="inv-form-group">
                  <label>Reorder Alert Level</label>
                  <input type="number" value={formData.reorderLevel} onChange={(e) => setFormData({...formData, reorderLevel: parseInt(e.target.value)})} min={1} />
                </div>
                <div className="inv-form-group">
                  <label>Storage Location</label>
                  <input value={formData.storageLocation} onChange={(e) => setFormData({...formData, storageLocation: e.target.value})} />
                </div>
              </div>
              <div className="inv-modal-footer">
                <button type="button" className="inv-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="inv-btn-primary">Create Item</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && selectedItem && (
        <div className="inv-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowPurchaseModal(false)}>
          <div className="inv-modal">
            <div className="inv-modal-header">
              <h3><ArrowDownCircle size={20} /> Record Purchase - {selectedItem.itemName}</h3>
              <button onClick={() => setShowPurchaseModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handlePurchase} className="inv-form">
              <div className="inv-form-row">
                <div className="inv-form-group">
                  <label>Purchase Date *</label>
                  <input type="date" value={purchaseData.purchaseDate} onChange={(e) => setPurchaseData({...purchaseData, purchaseDate: e.target.value})} required />
                </div>
                <div className="inv-form-group">
                  <label>Supplier Name</label>
                  <input value={purchaseData.supplierName} onChange={(e) => setPurchaseData({...purchaseData, supplierName: e.target.value})} placeholder="e.g., Uniforms Ltd" />
                </div>
              </div>
              <div className="inv-form-row">
                <div className="inv-form-group">
                  <label>Quantity *</label>
                  <input type="number" value={purchaseData.quantity} onChange={(e) => setPurchaseData({...purchaseData, quantity: parseInt(e.target.value)})} min={1} required />
                </div>
                <div className="inv-form-group">
                  <label>Unit Cost (KES)</label>
                  <input type="number" value={purchaseData.unitCost} onChange={(e) => setPurchaseData({...purchaseData, unitCost: parseFloat(e.target.value)})} min={0} />
                </div>
              </div>
              <div className="inv-form-row">
                <div className="inv-form-group">
                  <label>Invoice Number</label>
                  <input value={purchaseData.invoiceNumber} onChange={(e) => setPurchaseData({...purchaseData, invoiceNumber: e.target.value})} />
                </div>
                <div className="inv-form-group">
                  <label>Total Cost</label>
                  <input type="text" value={`KES ${(purchaseData.quantity * purchaseData.unitCost).toLocaleString()}`} readOnly className="readonly" />
                </div>
              </div>
              <div className="inv-form-group inv-form-full">
                <label>Notes</label>
                <textarea value={purchaseData.notes} onChange={(e) => setPurchaseData({...purchaseData, notes: e.target.value})} rows={2} />
              </div>
              <div className="inv-modal-footer">
                <button type="button" className="inv-btn-secondary" onClick={() => setShowPurchaseModal(false)}>Cancel</button>
                <button type="submit" className="inv-btn-primary">Record Purchase</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Distribute Modal */}
      {showDistributeModal && selectedItem && (
        <div className="inv-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowDistributeModal(false)}>
          <div className="inv-modal">
            <div className="inv-modal-header">
              <h3><ArrowUpCircle size={20} /> Distribute/Issue - {selectedItem.itemName}</h3>
              <button onClick={() => setShowDistributeModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleDistribute} className="inv-form">
              <div className="inv-form-row">
                <div className="inv-form-group">
                  <label>Distribute To (Guard) *</label>
                  <select value={distributeData.distributedToId} onChange={(e) => {
                    const guard = guards.find(g => g._id === e.target.value)
                    setDistributeData({
                      ...distributeData, 
                      distributedToId: e.target.value,
                      distributedTo: guard ? `${guard.firstName} ${guard.lastName}` : ''
                    })
                  }} required>
                    <option value="">Select Guard...</option>
                    {guards.map(g => (
                      <option key={g._id} value={g._id}>{g.firstName} {g.lastName} ({g.guardId})</option>
                    ))}
                  </select>
                </div>
                <div className="inv-form-group">
                  <label>Quantity *</label>
                     <input type="number" value={distributeData.quantity} onChange={(e) => setDistributeData({...distributeData, quantity: parseInt(e.target.value)})} min={1} max={selectedItem.remainingStock || 999999} required />
                </div>
              </div>
              <div className="inv-form-row">
                <div className="inv-form-group">
                  <label>Signed Off By *</label>
                  <input value={distributeData.signedOffBy} onChange={(e) => setDistributeData({...distributeData, signedOffBy: e.target.value})} placeholder="Authorizer name" required />
                </div>
                <div className="inv-form-group">
                  <label>Condition</label>
                  <select value={distributeData.condition} onChange={(e) => setDistributeData({...distributeData, condition: e.target.value})}>
                    {conditionOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="inv-form-group inv-form-full">
                <label>Purpose/Notes</label>
                <textarea value={distributeData.purpose} onChange={(e) => setDistributeData({...distributeData, purpose: e.target.value})} rows={2} placeholder="Reason for distribution..." />
              </div>
              <div className="inv-modal-footer">
                <button type="button" className="inv-btn-secondary" onClick={() => setShowDistributeModal(false)}>Cancel</button>
                <button type="submit" className="inv-btn-primary">Record Distribution</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Item Details Modal */}
      {viewItem && (
        <div className="inv-modal-overlay" onClick={(e) => e.target === e.currentTarget && setViewItem(null)}>
          <div className="inv-modal inv-modal-large">
            <div className="inv-modal-header">
              <h3><Eye size={20} /> {viewItem.itemName} Details</h3>
              <button onClick={() => setViewItem(null)}><X size={20} /></button>
            </div>
            <div className="inv-detail-body">
              <div className="inv-detail-section">
                <h4>Item Information</h4>
                <div className="inv-detail-grid">
                  <div><label>Item Code</label><span>{viewItem.itemCode || 'N/A'}</span></div>
                  <div><label>Category</label><span>{viewItem.category}</span></div>
                  <div><label>Sub Category</label><span>{viewItem.subCategory || 'N/A'}</span></div>
                  <div><label>Size</label><span>{viewItem.size || 'N/A'}</span></div>
                  <div><label>Color</label><span>{viewItem.color || 'N/A'}</span></div>
                  <div><label>Storage</label><span>{viewItem.storageLocation || 'Main Store'}</span></div>
                </div>
              </div>

              <div className="inv-detail-section">
                <h4>Stock Summary</h4>
                <div className="inv-stock-summary">
                  <div className="inv-stock-box">
                    <span className="inv-stock-number">{viewItem.totalPurchased || 0}</span>
                    <span>Total Purchased</span>
                  </div>
                  <div className="inv-stock-box">
                    <span className="inv-stock-number">{viewItem.totalDistributed || 0}</span>
                    <span>Total Distributed</span>
                  </div>
                  <div className="inv-stock-box highlight">
                    <span className="inv-stock-number">{viewItem.remainingStock || 0}</span>
                    <span>Remaining</span>
                  </div>
                </div>
              </div>

              {viewItem.purchaseLogs && viewItem.purchaseLogs.length > 0 && (
                <div className="inv-detail-section">
                  <h4><ArrowDownCircle size={16} /> Purchase History ({viewItem.purchaseLogs.length})</h4>
                  <table className="inv-log-table">
                    <thead>
                      <tr><th>Date</th><th>Supplier</th><th>Qty</th><th>Unit Cost</th><th>Total</th><th>Invoice</th></tr>
                    </thead>
                    <tbody>
                      {viewItem.purchaseLogs.map((log, idx) => (
                        <tr key={idx}>
                          <td>{formatDate(log.purchaseDate)}</td>
                          <td>{log.supplierName || 'N/A'}</td>
                          <td>{log.quantity}</td>
                          <td>KES {log.unitCost?.toLocaleString() || 0}</td>
                          <td>KES {log.totalCost?.toLocaleString() || 0}</td>
                          <td>{log.invoiceNumber || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewItem.distributionLogs && viewItem.distributionLogs.length > 0 && (
                <div className="inv-detail-section">
                  <h4><ArrowUpCircle size={16} /> Distribution History ({viewItem.distributionLogs.length})</h4>
                  <table className="inv-log-table">
                    <thead>
                      <tr><th>Date</th><th>Issued To</th><th>Qty</th><th>Signed Off By</th><th>Condition</th><th>Purpose</th></tr>
                    </thead>
                    <tbody>
                      {viewItem.distributionLogs.map((log, idx) => (
                        <tr key={idx}>
                          <td>{formatDate(log.distributedDate)}</td>
                          <td>{log.distributedTo}</td>
                          <td>{log.quantity}</td>
                          <td>{log.signedOffBy || 'N/A'}</td>
                          <td>{log.condition || 'Good'}</td>
                          <td>{log.purpose || log.notes || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="inv-modal-footer">
              <button className="inv-btn-secondary" onClick={() => setViewItem(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SupplyChain
